/**
 * Ligand rows for the Ligands tab, computed in the browser from the parsed models. Prod's table
 * (vendor-ref/mara/app/ui/src/components/nanome/WorkspaceLigands.vue) lists the ligands the server split out of each
 * entry, with RDKit properties; Lite lists one row per ligand residue (Substructure.Ligand: non-polymer, non-solvent,
 * non-ion, non-sugar) per entry, with the properties the atoms alone give: chain, atom count, formula and weight.
 *
 * The row id matches the default 'Ligand <chain>:<res>_<serial>' component (AddDefaultComponents.cs:253) so the eye
 * and zoom can find it.
 */
import { Substructure, type NanomeModel } from '@nanome-lite/data/model';
import { elementSymbol, writeSdf } from '@nanome-lite/bridge/src/converters';
import type { Component, Entry } from '@nanome-lite/state';

export interface LigandRow {
  /** `${entrySerial}:${chain}:${resName}_${serial}${insCode}`, unique per scene */
  id: string;
  entrySerial: number;
  entry: string;
  /** residue name (CCD code) */
  name: string;
  chain: string;
  residueSerial: number;
  insCode: string;
  /** residue index in the model */
  residueIndex: number;
  /** 'C:HEM_142', the label default components use */
  label: string;
  atomCount: number;
  /** Hill-order formula of the atoms in the file */
  formula: string;
  /** g/mol, summed over the atoms in the file (hydrogens only when the file has them) */
  weight: number;
}

/** Standard atomic weights (IUPAC 2021 abridged), index = atomic number. 0 for unknown. */
const ATOMIC_WEIGHT: readonly number[] = [0,
  1.008, 4.0026, 6.94, 9.0122, 10.81, 12.011, 14.007, 15.999, 18.998, 20.180,
  22.990, 24.305, 26.982, 28.085, 30.974, 32.06, 35.45, 39.95, 39.098, 40.078,
  44.956, 47.867, 50.942, 51.996, 54.938, 55.845, 58.933, 58.693, 63.546, 65.38,
  69.723, 72.630, 74.922, 78.971, 79.904, 83.798, 85.468, 87.62, 88.906, 91.224,
  92.906, 95.95, 97, 101.07, 102.91, 106.42, 107.87, 112.41, 114.82, 118.71,
  121.76, 127.60, 126.90, 131.29, 132.91, 137.33, 138.91, 140.12, 140.91, 144.24,
  145, 150.36, 151.96, 157.25, 158.93, 162.50, 164.93, 167.26, 168.93, 173.05,
  174.97, 178.49, 180.95, 183.84, 186.21, 190.23, 192.22, 195.08, 196.97, 200.59,
  204.38, 207.2, 208.98, 209, 210, 222, 223, 226, 227, 232.04, 231.04, 238.03,
];

export const atomicWeight = (z: number): number => ATOMIC_WEIGHT[z] ?? 0;

/** Hill system: C first, then H, then the rest alphabetically; without carbon everything is alphabetical. */
export function hillFormula(counts: Map<string, number>): string {
  const keys = [...counts.keys()].filter(k => k !== 'X');
  const hasC = counts.has('C');
  keys.sort((a, b) => {
    if (hasC) {
      const rank = (s: string) => (s === 'C' ? 0 : s === 'H' ? 1 : 2);
      const d = rank(a) - rank(b);
      if (d) return d;
    }
    return a.localeCompare(b);
  });
  return keys.map(k => `${k}${counts.get(k)! > 1 ? counts.get(k) : ''}`).join('');
}

/** One row per ligand residue of the model, in file order. */
export function ligandsOfModel(entry: Pick<Entry, 'serial' | 'name'>, model: NanomeModel): LigandRow[] {
  const r = model.residues;
  const rows: LigandRow[] = [];
  for (let ri = 0; ri < r.count; ri++) {
    if (r.substructure[ri] !== Substructure.Ligand) continue;
    const start = r.atomStart[ri]!, count = r.atomCount[ri]!;
    const counts = new Map<string, number>();
    let weight = 0;
    for (let a = start; a < start + count; a++) {
      const z = model.element[a]!;
      const sym = elementSymbol(z);
      counts.set(sym, (counts.get(sym) ?? 0) + 1);
      weight += atomicWeight(z);
    }
    const chain = model.chains.name[r.chainIndex[ri]!] ?? '';
    const name = r.name[ri]!, serial = r.serial[ri]!, insCode = r.insCode[ri] ?? '';
    rows.push({
      id: `${entry.serial}:${chain}:${name}_${serial}${insCode}`,
      entrySerial: entry.serial,
      entry: entry.name,
      name,
      chain,
      residueSerial: serial,
      insCode,
      residueIndex: ri,
      label: `${chain}:${name}_${serial}`,
      atomCount: count,
      formula: hillFormula(counts),
      weight: Math.round(weight * 100) / 100,
    });
  }
  return rows;
}

/** Ligand rows for every loaded entry, entries in load order. */
export function deriveLigands(entries: readonly Pick<Entry, 'serial' | 'name'>[], models: ReadonlyMap<number, NanomeModel>): LigandRow[] {
  return entries.flatMap((e) => {
    const model = models.get(e.serial);
    return model ? ligandsOfModel(e, model) : [];
  });
}

/** The default 'Ligand C:HEM_142' component of a row (first match), if the user has not removed or renamed it. */
export function ligandComponent(row: LigandRow, components: readonly Component[]): Component | undefined {
  const name = `Ligand ${row.label}`;
  return components.find(c => c.name === name && c.entrySerials.length === 1 && c.entrySerials[0] === row.entrySerial);
}

/** A one-record SDF of the ligand residue: its atoms at the current frame and the bonds between them. */
export function ligandSdf(row: LigandRow, model: NanomeModel): string {
  const start = model.residues.atomStart[row.residueIndex]!, n = model.residues.atomCount[row.residueIndex]!;
  const xyz = model.xyz.slice(3 * start, 3 * (start + n));
  const a: number[] = [], b: number[] = [], order: number[] = [], aromatic: number[] = [];
  for (let i = 0; i < model.bonds.count; i++) {
    const p = model.bonds.a[i]!, q = model.bonds.b[i]!;
    if (p < start || q < start || p >= start + n || q >= start + n) continue;
    a.push(p - start); b.push(q - start); order.push(model.bonds.order[i]!); aromatic.push(model.bonds.aromatic[i]!);
  }
  const sub = {
    atomCount: n,
    element: model.element.slice(start, start + n),
    formalCharge: model.formalCharge.slice(start, start + n),
    xyz,
    frames: [xyz],
    bonds: { count: a.length, a: Uint32Array.from(a), b: Uint32Array.from(b), order: Uint8Array.from(order), aromatic: Uint8Array.from(aromatic) },
  } as unknown as NanomeModel;
  return writeSdf(sub, { title: `${row.entry} ${row.label}`, allFrames: false });
}

/** File name in prod's getSdfFilename shape: `${entry}_${name}.sdf`, ':' replaced. */
export function ligandSdfFilename(row: LigandRow): string {
  return `${row.entry}_${row.label}.sdf`.replaceAll(':', '_');
}
